import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Sparkles,
  Mountain,
  Heart,
  Eye,
  RefreshCw,
  BookOpen,
  ArrowLeft,
  Target,
  Zap,
  MessageCircle,
  Search,
  CheckCircle,
  Palette,
  Repeat,
  Lightbulb,
  Trophy,
  Star,
  HardDrive,
  Cloud,
  Check,
  X,
} from 'lucide-react'
import { Button, Card, CardContent } from '@/components/ui'
import { WaitlistSignup } from '@/components/WaitlistSignup'
import { LogoSvg } from '@/assets/brand/logos'

// Grouped themes with category labels
const themeGroups = [
  {
    label: 'Foundations',
    themes: [
      {
        id: 'story-shape',
        icon: Mountain,
        title: 'Stories Have Shape',
        tagline: 'Stories move forward on purpose.',
        color: 'var(--color-kids-primary)',
        bullets: [
          'Every story has a beginning, middle, and end.',
          'Problems make stories interesting.',
          'Change makes endings satisfying.',
        ],
      },
      {
        id: 'character-wants',
        icon: Heart,
        title: 'Characters Want Something',
        tagline: 'Every good story starts with someone who wants something.',
        color: 'var(--color-kids-secondary)',
        bullets: [
          'Characters move stories forward.',
          'Give your hero something to chase.',
          'Give them something to fear.',
        ],
      },
    ],
  },
  {
    label: 'Craft',
    themes: [
      {
        id: 'show-dont-tell',
        icon: Eye,
        title: 'Show, Don\'t Tell',
        tagline: 'Great writing helps you see the story.',
        color: 'var(--color-kids-accent)',
        bullets: [
          'Use your senses.',
          'Use action instead of labels.',
          'Concrete details paint pictures.',
        ],
      },
      {
        id: 'writers-revise',
        icon: RefreshCw,
        title: 'Writers Revise',
        tagline: 'Good writing is rewriting.',
        color: 'var(--color-kids-warning)',
        bullets: [
          'First drafts are messy — that\'s okay.',
          'Revision makes writing better.',
          'Small changes add up.',
        ],
        mantra: 'First draft = messy. Second draft = better. Third draft = proud.',
      },
    ],
  },
  {
    label: 'Growth',
    themes: [
      {
        id: 'readers-writers',
        icon: BookOpen,
        title: 'Borrow and Transform',
        tagline: 'Great writers learn from other writers.',
        color: 'var(--color-kids-success)',
        bullets: [
          'Imitation is part of learning.',
          'Retell stories in your own way.',
          'Perspective changes meaning.',
        ],
        mantra: 'Writers are also readers. Readers are also writers.',
      },
    ],
  },
]

const badgeGroups = [
  {
    title: 'Story Shape',
    color: 'var(--color-kids-primary)',
    bgTint: 'var(--color-kids-primary)',
    badges: [
      { icon: Target, name: 'Beginning Builder', desc: 'You set the scene and showed what your hero wants.' },
      { icon: Zap, name: 'Problem Finder', desc: 'You created a real obstacle that matters.' },
      { icon: Star, name: 'Big Moment Maker', desc: 'You wrote a powerful turning point.' },
    ],
    totalCount: 5,
  },
  {
    title: 'Craft',
    color: 'var(--color-kids-accent)',
    bgTint: 'var(--color-kids-accent)',
    badges: [
      { icon: Zap, name: 'Verb Power Hero', desc: 'You replaced boring verbs with vivid ones.' },
      { icon: Eye, name: 'Senses Star', desc: 'You helped us see, hear, or feel the moment.' },
      { icon: MessageCircle, name: 'Dialogue Detective', desc: 'You used clear conversation with punctuation.' },
    ],
    totalCount: 5,
  },
  {
    title: 'Revision',
    color: 'var(--color-kids-warning)',
    bgTint: 'var(--color-kids-warning)',
    badges: [
      { icon: Search, name: 'Detail Detective', desc: 'You made your writing clearer and richer.' },
      { icon: Sparkles, name: 'Clear-as-Crystal', desc: 'You fixed confusing spots.' },
      { icon: RefreshCw, name: 'Sentence Shiner', desc: 'You improved tricky sentences.' },
    ],
    totalCount: 4,
  },
  {
    title: 'Reader-Writer',
    color: 'var(--color-kids-success)',
    bgTint: 'var(--color-kids-success)',
    badges: [
      { icon: Palette, name: 'Style Apprentice', desc: 'You borrowed a great writer\'s style.' },
      { icon: Repeat, name: 'Story Translator', desc: 'You retold a story in your own way.' },
      { icon: Lightbulb, name: 'Big-Idea Thinker', desc: 'You connected the story to a deeper meaning.' },
    ],
    totalCount: 4,
  },
]

const storySteps = [
  {
    name: 'Beginning',
    label: 'Meet your hero',
    shortLabel: 'Where are we? Who is your hero?',
    questions: [
      'What is your hero\'s name?',
      'Where are they?',
      'What do they want most?',
    ],
    helper: 'A strong beginning makes us care.',
    color: 'var(--color-kids-primary)',
  },
  {
    name: 'Problem',
    label: 'Something stands in the way',
    shortLabel: 'Something blocks your hero.',
    questions: [
      'What problem appears?',
      'Why does it matter?',
      'How does your hero feel?',
    ],
    helper: 'No problem, no story.',
    color: 'var(--color-kids-secondary)',
  },
  {
    name: 'Adventure',
    label: 'Try, Fail, Try Again',
    shortLabel: 'What does your hero try?',
    questions: [
      'What do they try first?',
      'What goes wrong?',
      'What do they try next?',
    ],
    helper: 'Stories grow through struggle.',
    color: 'var(--color-kids-accent)',
  },
  {
    name: 'Climax',
    label: 'The Big Moment',
    shortLabel: 'The hardest choice or action.',
    questions: [
      'What is the biggest moment?',
      'What does your hero decide?',
      'What is at risk?',
    ],
    helper: 'This is the heart of your story.',
    color: 'var(--color-kids-warning)',
  },
  {
    name: 'Resolution',
    label: 'What Changed?',
    shortLabel: 'How does it end?',
    questions: [
      'What changed for your hero?',
      'What did they learn?',
      'How does the story feel at the end?',
    ],
    helper: 'A good ending shows growth.',
    color: 'var(--color-kids-success)',
  },
]

export function HowItWorks() {
  return (
    <div className="min-h-screen bg-[var(--color-kids-bg)]">
      {/* Sticky Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-[var(--color-kids-border)]">
        <div className="container mx-auto pl-7 pr-6 h-[76px] flex items-center justify-between">
          {/* Back link - demoted */}
          <Link
            to="/"
            className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-500 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </Link>

          {/* Logo - brand anchor with subtle hover */}
          <div className="absolute left-1/2 -translate-x-1/2">
            <Link to="/" className="block transition-transform duration-200 ease-out hover:scale-[1.02] hover:opacity-90">
              <img src={LogoSvg} alt="Inkwell Jr." className="h-[44px] md:h-[52px]" />
            </Link>
          </div>

          {/* Coming Soon Pill - secondary status */}
          <span className="px-3 py-1.5 rounded-full bg-slate-50 text-slate-400 font-medium text-xs ring-1 ring-slate-200">
            Coming Soon
          </span>
        </div>
      </header>

      {/* Hero - with emotional hook */}
      <section className="container mx-auto px-6 py-16 md:py-20 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto"
        >
          {/* Emotional hook - secondary, lead-in */}
          <p className="text-sm font-medium text-slate-500 mb-2">
            Every great writer starts with a single story
          </p>

          {/* Main headline */}
          <h1 className="font-display text-4xl md:text-5xl font-bold text-[var(--color-kids-text)] mb-6">
            How Stories Come to Life
          </h1>

          {/* Tightened copy */}
          <p className="text-lg text-[var(--color-kids-text-muted)] max-w-2xl mx-auto leading-relaxed">
            Every great writer starts with a story they care about.
            In Inkwell Jr., writing is not just practice — it is a journey.
            Each mission helps your child explore ideas, make choices, and learn how stories come to life.
          </p>
        </motion.div>

        {/* Softer top CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-10"
        >
          <WaitlistSignup variant="hero" />
        </motion.div>
      </section>

      {/* Five Big Ideas - with category grouping */}
      <section className="container mx-auto px-6 pb-20">
        {/* Narrative bridge */}
        <p className="text-center text-[var(--color-kids-text-muted)] mb-3 max-w-lg mx-auto">
          Great stories aren't random. They follow patterns writers have used for generations.
        </p>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-center text-[var(--color-kids-text)] mb-12">
          Five Big Ideas About Writing
        </h2>

        {themeGroups.map((group, groupIndex) => (
          <div key={group.label} className="mb-10 last:mb-0">
            {/* Category label */}
            <p className="text-xs font-semibold uppercase tracking-wider text-[var(--color-kids-text-muted)]/60 mb-4 ml-1">
              {group.label}
            </p>

            <div className={`grid gap-6 ${group.themes.length === 1 ? 'md:grid-cols-1 max-w-md' : 'md:grid-cols-2'}`}>
              {group.themes.map((theme, index) => (
                <motion.div
                  key={theme.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: (groupIndex * 0.1) + (index * 0.1) }}
                >
                  <Card hoverable className="h-full">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4 mb-4">
                        <div
                          className="w-12 h-12 rounded-2xl flex items-center justify-center"
                          style={{ background: `color-mix(in srgb, ${theme.color} 15%, transparent)` }}
                        >
                          <theme.icon className="w-6 h-6" style={{ color: theme.color }} />
                        </div>
                        <h3 className="font-display text-xl font-bold text-[var(--color-kids-text)]">
                          {theme.title}
                        </h3>
                      </div>
                      <p className="text-[var(--color-kids-primary)] font-medium mb-4">
                        {theme.tagline}
                      </p>
                      <ul className="space-y-2">
                        {theme.bullets.map((bullet) => (
                          <li key={bullet} className="flex items-start gap-2 text-[var(--color-kids-text-muted)]">
                            <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: theme.color }} />
                            <span>{bullet}</span>
                          </li>
                        ))}
                      </ul>
                      {theme.mantra && (
                        <p className="mt-4 p-3 rounded-lg bg-[var(--color-kids-surface-alt)] text-[var(--color-kids-text)] text-sm font-medium">
                          {theme.mantra}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        ))}
      </section>

      {/* Story Steps - THE CENTERPIECE */}
      <section className="bg-[var(--color-kids-surface)] pt-24 pb-20 md:pt-28 md:pb-24">
        <div className="container mx-auto px-6">
          {/* Narrative intro */}
          <p className="text-center text-[var(--color-kids-primary)] font-medium mb-3">
            Every story has a beginning, a challenge, and a change
          </p>

          <h2 className="font-display text-3xl md:text-4xl font-bold text-center text-[var(--color-kids-text)] mb-4">
            Your Story Steps
          </h2>
          <p className="text-center text-[var(--color-kids-text-muted)] mb-12 max-w-xl mx-auto">
            Every story is a little adventure. In Inkwell Jr., your adventure follows five steps.
          </p>

          {/* Story Mountain Visual - enhanced */}
          <div className="flex justify-center mb-14">
            <div className="flex items-end gap-3 md:gap-5">
              {storySteps.map((step, index) => {
                const heights = ['h-20', 'h-28', 'h-36', 'h-44', 'h-28']
                return (
                  <motion.div
                    key={step.name}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="flex flex-col items-center"
                  >
                    <div
                      className={`w-14 md:w-20 ${heights[index]} rounded-t-xl flex items-end justify-center pb-3 shadow-sm`}
                      style={{ background: step.color }}
                    >
                      <span className="text-white text-sm font-bold">{index + 1}</span>
                    </div>
                    <span className="text-sm font-semibold text-[var(--color-kids-text)] mt-3 text-center">
                      {step.name}
                    </span>
                  </motion.div>
                )
              })}
            </div>
          </div>

          {/* Step Details */}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {storySteps.map((step, index) => (
              <motion.div
                key={step.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
              >
                <Card className="h-full border-t-4" style={{ borderTopColor: step.color }}>
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <span
                        className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm"
                        style={{ background: step.color }}
                      >
                        {index + 1}
                      </span>
                      <div>
                        <h3 className="font-display font-bold text-[var(--color-kids-text)]">
                          {step.name}
                        </h3>
                        <p className="text-sm text-[var(--color-kids-text-muted)]">{step.label}</p>
                      </div>
                    </div>
                    <p className="text-[var(--color-kids-text)] mb-3 font-medium">
                      {step.shortLabel}
                    </p>
                    <ul className="space-y-1 mb-3">
                      {step.questions.map((q) => (
                        <li key={q} className="text-sm text-[var(--color-kids-text-muted)] flex items-start gap-2">
                          <span style={{ color: step.color }}>•</span>
                          {q}
                        </li>
                      ))}
                    </ul>
                    <p className="text-xs italic text-[var(--color-kids-text-muted)] pt-2 border-t border-[var(--color-kids-border)]">
                      {step.helper}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Badges - reduced density with visual differentiation */}
      <section className="container mx-auto px-6 py-20">
        {/* Narrative bridge */}
        <p className="text-center text-[var(--color-kids-primary)] font-medium mb-3">
          Badges celebrate real progress
        </p>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-center text-[var(--color-kids-text)] mb-4">
          Badges You Can Earn
        </h2>
        <p className="text-center text-[var(--color-kids-text-muted)] mb-12 max-w-xl mx-auto">
          Each badge marks a skill you've practiced and made your own.
        </p>

        <div className="grid gap-8 md:grid-cols-2 max-w-5xl mx-auto">
          {badgeGroups.map((group, groupIndex) => (
            <motion.div
              key={group.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: groupIndex * 0.1 }}
              className="rounded-2xl p-6"
              style={{ background: `color-mix(in srgb, ${group.bgTint} 5%, transparent)` }}
            >
              <h3
                className="font-display text-lg font-bold mb-4 flex items-center gap-2"
                style={{ color: group.color }}
              >
                <Trophy className="w-5 h-5" />
                {group.title}
              </h3>
              <div className="space-y-3">
                {group.badges.map((badge) => (
                  <div
                    key={badge.name}
                    className="flex items-center gap-3 p-3 rounded-xl bg-white shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ background: `color-mix(in srgb, ${group.color} 15%, transparent)` }}
                    >
                      <badge.icon className="w-5 h-5" style={{ color: group.color }} />
                    </div>
                    <div className="min-w-0">
                      <p className="font-bold text-[var(--color-kids-text)] text-sm">{badge.name}</p>
                      <p className="text-xs text-[var(--color-kids-text-muted)] truncate">{badge.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              {group.totalCount > group.badges.length && (
                <p className="text-xs text-[var(--color-kids-text-muted)] mt-3 text-center">
                  + {group.totalCount - group.badges.length} more badges to discover
                </p>
              )}
            </motion.div>
          ))}
        </div>
      </section>

      {/* Choose Your Studio - with narrative framing */}
      <section className="bg-[var(--color-kids-surface)] py-20">
        <div className="container mx-auto px-6">
          {/* Narrative bridge */}
          <p className="text-center text-[var(--color-kids-primary)] font-medium mb-3">
            Start simple. Grow as your stories grow.
          </p>

          <h2 className="font-display text-2xl md:text-3xl font-bold text-center text-[var(--color-kids-text)] mb-4">
            Choose Your Writing Studio
          </h2>
          <p className="text-center text-[var(--color-kids-text-muted)] mb-12 max-w-xl mx-auto">
            Every writer starts somewhere. Pick the studio that fits where you are now.
          </p>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* Local Studio - Free */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Card className="h-full bg-white/70 border-slate-200">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
                      <HardDrive className="w-6 h-6 text-slate-400" />
                    </div>
                    <div>
                      <h3 className="font-display text-xl font-bold text-[var(--color-kids-text)]">
                        Local Studio
                      </h3>
                      <p className="text-[var(--color-kids-success)] font-semibold">Free</p>
                    </div>
                  </div>

                  {/* Outcome-focused description */}
                  <p className="text-[var(--color-kids-text)] font-medium mb-2">
                    Write your first stories
                  </p>
                  <p className="text-[var(--color-kids-text-muted)] mb-6 text-sm">
                    Perfect for trying out Inkwell Jr. and discovering your writing voice.
                  </p>

                  <ul className="space-y-3 mb-6">
                    <li className="flex items-center gap-3 text-[var(--color-kids-text)]">
                      <Check className="w-5 h-5 text-[var(--color-kids-success)] flex-shrink-0" />
                      <span>Full writing curriculum</span>
                    </li>
                    <li className="flex items-center gap-3 text-[var(--color-kids-text)]">
                      <Check className="w-5 h-5 text-[var(--color-kids-success)] flex-shrink-0" />
                      <span>Daily missions</span>
                    </li>
                    <li className="flex items-center gap-3 text-[var(--color-kids-text)]">
                      <Check className="w-5 h-5 text-[var(--color-kids-success)] flex-shrink-0" />
                      <span>1 story per child</span>
                    </li>
                    <li className="flex items-center gap-3 text-[var(--color-kids-text-muted)]">
                      <X className="w-5 h-5 opacity-40 flex-shrink-0" />
                      <span>Saved on this device only</span>
                    </li>
                  </ul>

                  <Button variant="secondary" size="lg" className="w-full opacity-60 cursor-not-allowed" disabled>
                    Coming Soon
                  </Button>
                </CardContent>
              </Card>
            </motion.div>

            {/* Cloud Studio - Premium */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Card className="h-full border-2 border-[var(--color-kids-primary)] relative overflow-hidden shadow-xl ring-1 ring-[var(--color-kids-primary)]/20">
                <div className="absolute top-0 right-0 bg-[var(--color-kids-primary)] text-white text-xs font-bold px-4 py-1.5 rounded-bl-xl">
                  RECOMMENDED
                </div>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center"
                      style={{ background: 'color-mix(in srgb, var(--color-kids-primary) 15%, transparent)' }}
                    >
                      <Cloud className="w-6 h-6 text-[var(--color-kids-primary)]" />
                    </div>
                    <div>
                      <h3 className="font-display text-xl font-bold text-[var(--color-kids-text)]">
                        Cloud Studio
                      </h3>
                      <p className="text-[var(--color-kids-primary)] font-semibold">$4.99/month</p>
                    </div>
                  </div>

                  {/* Outcome-focused description */}
                  <p className="text-[var(--color-kids-text)] font-medium mb-2">
                    Build a portfolio and grow as a storyteller
                  </p>
                  <p className="text-[var(--color-kids-text-muted)] mb-6 text-sm">
                    Unlimited stories, synced across devices, with progress tracking and badges.
                  </p>

                  <ul className="space-y-3 mb-6">
                    <li className="flex items-center gap-3 text-[var(--color-kids-text)]">
                      <Check className="w-5 h-5 text-[var(--color-kids-success)] flex-shrink-0" />
                      <span>Everything in Free, plus...</span>
                    </li>
                    <li className="flex items-center gap-3 text-[var(--color-kids-text)]">
                      <Check className="w-5 h-5 text-[var(--color-kids-success)] flex-shrink-0" />
                      <span><strong>Unlimited stories</strong></span>
                    </li>
                    <li className="flex items-center gap-3 text-[var(--color-kids-text)]">
                      <Check className="w-5 h-5 text-[var(--color-kids-success)] flex-shrink-0" />
                      <span>Cloud sync across devices</span>
                    </li>
                    <li className="flex items-center gap-3 text-[var(--color-kids-text)]">
                      <Check className="w-5 h-5 text-[var(--color-kids-success)] flex-shrink-0" />
                      <span>Badges and achievements</span>
                    </li>
                  </ul>

                  <Button size="lg" className="w-full opacity-60 cursor-not-allowed" disabled>
                    Coming Soon
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          <p className="text-center text-sm text-[var(--color-kids-text-muted)] mt-8 max-w-lg mx-auto">
            Start free, then upgrade anytime. Your local story automatically syncs to the cloud when you upgrade.
          </p>
        </div>
      </section>

      {/* CTA - emotional framing */}
      <section className="bg-[var(--color-kids-primary)] py-16 md:py-20">
        <div className="container mx-auto px-6 text-center">
          <p className="text-white/60 text-xs font-medium mb-3 max-w-md mx-auto">
            Give your child a place to begin.
          </p>
          <h2 className="font-display text-2xl md:text-3xl font-bold text-white mb-4">
            Be First to Try Inkwell Jr.
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-xl mx-auto">
            Join the first wave of young storytellers. We'll email you when early access opens.
          </p>
          <WaitlistSignup variant="cta" />
        </div>
      </section>

      {/* Footer */}
      <footer className="container mx-auto px-6 py-10 text-center">
        <p className="text-sm text-[var(--color-kids-text-muted)]">
          Made with love for young storytellers everywhere.
        </p>
      </footer>
    </div>
  )
}
